<script setup lang="ts">
import { track } from '@/lib/usageTracker'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  postStockAsync,
  type StockTaskDetailDTO,
  postItemAsync,
  postItemsUnassociated,
  type ItemAssociationRow,
  type QboItemSearchResult,
  searchQboItemsPaged,
  getItemLink,
  postItemLink,
} from '@/api/lytAccountingClient'
import { type ItemTaskDetailDTO, type TaskDetail, type TaskSummaryDto } from '@/api/lytAccountingClient'
import { Alert, Button, Badge, Pagination, TabNav, SearchInput } from '@/components/ui'
import TaskDetailDrawer from '@/components/task/TaskDetailDrawer.vue'
import { useTaskActivityStore } from '@/stores/taskActivityStore'
import { useEntityLinkStudio } from '@/composables/useEntityLinkStudio'
import {
  invalidateItemAssociationsQueries,
  useItemAssociationsQuery,
} from '@/composables/queries/useItemAssociationsQuery'
import EntityAssociationDialog from '@/components/entity/EntityAssociationDialog.vue'
import { Package, Link, AlertCircle, CheckCircle, History, RefreshCw } from 'lucide-vue-next'
type ItemPageTask = StockTaskDetailDTO | ItemTaskDetailDTO | TaskDetail | TaskSummaryDto

// --- State ---
const selectedTask = ref<any | null>(null)
const drawerOpen = ref(false)
const syncing = ref(false)
const actionError = ref('')
const successMessage = ref('')
const taskActivity = useTaskActivityStore()
let unsubscribeTaskActivity: (() => void) | null = null
const router = useRouter()

// --- Association table state (page owns filters; Colada owns the read) ---
const associationPage = ref(1)
const associationPageSize = ref(Number(localStorage.getItem('items.pageSize') || 50))
const associationStatus = ref<'all' | 'associated' | 'unassociated' | 'syncing'>('all')
const syncingRow = ref<string | null>(null)
// --- Tab and dialog state ---
const activeTab = ref<'lytning' | 'qbo'>('lytning')
const dialogOpen = ref(false)
const dialogSource = ref<'lytning' | 'qbo'>('lytning')
const dialogLytningEntity = ref<ItemAssociationRow | null>(null)
const dialogQboEntity = ref<QboItemSearchResult | null>(null)
const syncingAllFeedback = ref('')
const syncingAllLoading = ref(false)
const lastRefreshAt = ref<Date | null>(null)

const {
  rows: associationRows,
  total: associationTotal,
  isLoading: associationLoading,
  errorMessage: associationQueryError,
  refetch: refetchAssociations,
} = useItemAssociationsQuery({
  page: associationPage,
  pageSize: associationPageSize,
  status: associationStatus,
})

// Prefer action errors; fall back to Colada query errors for the banner.
const error = computed(() => {
  if (actionError.value) return actionError.value
  return associationQueryError.value ?? ''
})

// Stamp last successful payload so the refresh label stays meaningful.
watch(
  () => associationTotal.value,
  () => {
    if (!associationLoading.value && !associationQueryError.value) {
      lastRefreshAt.value = new Date()
    }
  },
)

const searchQuery = ref('')
const sharedFilter = ref<'all' | 'associated' | 'unassociated' | 'syncing'>('all')
const filteredLytningRows = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return associationRows.value
  return associationRows.value.filter((row) => {
    const haystack = [row.lytsoftItemId, row.qboItemId, row.itemName, row.itemType]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase())
      .join(' ')
    return haystack.includes(q)
  })
})

function mapUiFilterToStudio(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  if (filter === 'associated') return 'linked' as const
  if (filter === 'unassociated') return 'unlinked' as const
  return filter
}

function onSharedFilterClick(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  // Cancel any pending debounced search so the filter click
  // is the single source of truth for issuing a new search
  // (setAssociationFilter triggers searchQbo internally).
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  track('btn:ItemFilter', 'click')
  sharedFilter.value = filter
  if (activeTab.value === 'lytning') {
    setAssociationStatus(filter)
  } else {
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(filter))
  }
}
const studio = useEntityLinkStudio<QboItemSearchResult>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchPredicate: searchQboItemsPaged as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkAction: (qboId, lytId) => postItemLink(qboId, lytId) as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reverseLookupAction: (qboId, lytId) => getItemLink(qboId, lytId) as any,
  entityLabel: 'item',
})
// --- Dialog handlers (Phase 24) ---
async function handleAssociated() {
  dialogOpen.value = false
  // Successful link: invalidate every item-associations cache entry so the active list refetches.
  await Promise.all([invalidateItemAssociationsQueries(), loadTaskState()])
}

function openLinkDialog(source: 'lytning' | 'qbo', entity?: ItemAssociationRow | QboItemSearchResult) {
  if (source === 'lytning') {
    dialogLytningEntity.value = entity as ItemAssociationRow | null
    dialogQboEntity.value = null
  } else {
    dialogQboEntity.value = entity as QboItemSearchResult | null
    dialogLytningEntity.value = null
  }
  dialogSource.value = source
  dialogOpen.value = true
}
// --- Computed ---

const associatedCount = computed(() =>
  associationStatus.value === 'unassociated' || associationStatus.value === 'syncing'
    ? 0
    : associationRows.value.filter(r => r.qboItemId).length
)

const unassociatedCount = computed(() =>
  associationStatus.value === 'associated' || associationStatus.value === 'syncing'
    ? 0
    : associationRows.value.filter(r => !r.qboItemId).length
)

const syncingCount = computed(() =>
  associationRows.value.filter(r => r.runningTaskId).length
)

// --- Actions ---
async function triggerStockSync() {
  syncing.value = true
  actionError.value = ''
  successMessage.value = ''
  try {
    const res = await postStockAsync({ scope: 'full-sync' })
    successMessage.value = `Stock sync started (task ${res.taskId})`
  } catch (e) {
    actionError.value = (e as Error)?.message || 'Sync failed'
  } finally {
    syncing.value = false
  }
}

async function loadTaskState() {
  track('btn:RefreshItems', 'click')
  await taskActivity.refreshNow({
    includeRecent: true,
    includeActive: true,
    entityTypes: ['STOCK', 'ITEM'],
    recentLimit: 50,
  })
}

// --- Association table actions ---
async function loadAssociations() {
  syncingAllFeedback.value = ''
  actionError.value = ''
  await refetchAssociations()
}

async function syncSingleItem(item: ItemAssociationRow) {
  if (item.runningTaskId) return
  syncingRow.value = item.lytsoftItemId
  actionError.value = ''
  try {
    await postItemAsync({ scope: 'single-sync', entityId: item.lytsoftItemId })
    // Backend registration is not instantaneous; keep the 2s delay, then
    // targeted invalidation instead of a manual reload.
    setTimeout(() => {
      void invalidateItemAssociationsQueries()
    }, 2000)
  } catch (e) {
    actionError.value = (e as Error)?.message || 'Sync failed'
  } finally {
    syncingRow.value = null
  }
}

async function syncAllUnassociated() {
  syncingAllFeedback.value = ''
  syncingAllLoading.value = true
  actionError.value = ''
  try {
    const res = await postItemsUnassociated()
    if (res.taskId) {
      syncingAllFeedback.value = `Bulk sync started (${res.totalUnassociated} items in batches, task ${res.taskId})`
      // Backend registration is not instantaneous; same delay as single-sync.
      setTimeout(() => {
        void invalidateItemAssociationsQueries()
      }, 2000)
    } else {
      syncingAllFeedback.value = 'No unassociated items to sync'
    }
  } catch (e) {
    actionError.value = (e as Error)?.message || 'Bulk sync failed'
  } finally {
    syncingAllLoading.value = false
  }
}

function setAssociationStatus(status: 'all' | 'associated' | 'unassociated' | 'syncing') {
  associationStatus.value = status
  associationPage.value = 1
  // Reactive key change drives Colada; clear local feedback without forced refetch.
  syncingAllFeedback.value = ''
  actionError.value = ''
}

function onPageSizeChange(size: number) {
  associationPageSize.value = size
  localStorage.setItem('items.pageSize', String(size))
  associationPage.value = 1
  // Reactive key change drives Colada; clear local feedback without forced refetch.
  syncingAllFeedback.value = ''
  actionError.value = ''
}

// Replay the shared filter when the active tab changes so filter state persists.
watch(activeTab, (tab) => {
  if (tab === 'lytning') {
    setAssociationStatus(sharedFilter.value)
  } else {
    // Sync studio.query BEFORE setAssociationFilter so the
    // composable's internal searchQbo picks up the typed query.
    // Cancel any pending debounced search to avoid duplicate requests.
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
      searchDebounceTimer = null
    }
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(sharedFilter.value))
  }
})

// Single searchQuery drives both tabs: Lytning filters in-memory; QBO debounces to studio.
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
watch(searchQuery, (q) => {
  if (activeTab.value === 'qbo') {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      void studio.searchQbo(q, 1)
    }, 300)
  }
})


function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString()
}

onMounted(() => {
  unsubscribeTaskActivity = taskActivity.subscribeActivity({
    includeRecent: true,
    includeActive: true,
    entityTypes: ['STOCK', 'ITEM'],
    recentLimit: 50,
  })
  // useQuery already fetches on setup; do not refetch here.
})

onUnmounted(() => {
  unsubscribeTaskActivity?.()
  unsubscribeTaskActivity = null
})

function openTaskDrawer(task: any) {
  selectedTask.value = task
  drawerOpen.value = true
}

function taskEntityId(task: ItemPageTask): string {
  return 'entityId' in task && task.entityId ? task.entityId : ('externalId' in task && task.externalId ? task.externalId : 'N/A')
}

function taskAcceptedAt(task: ItemPageTask): string {
  return 'acceptedAt' in task && task.acceptedAt ? task.acceptedAt : ('createdAt' in task ? task.createdAt || '' : '')
}

function taskTotalChildren(task: ItemPageTask): number {
  return 'totalChildren' in task ? task.totalChildren ?? 0 : 0
}

function taskFailedChildren(task: ItemPageTask): number {
  return 'failedChildren' in task ? task.failedChildren ?? 0 : 0
}

function taskCompletedChildren(task: ItemPageTask): number {
  return 'completedChildren' in task ? task.completedChildren ?? 0 : 0
}

function taskErrorMessage(task: ItemPageTask): string | null {
  if ('lastErrorMessage' in task && task.lastErrorMessage) return task.lastErrorMessage
  if ('errorMessage' in task && task.errorMessage) return task.errorMessage
  return null
}
</script>

<template>
  <div class="app-page app-page--narrow">
    <section class="app-page-header">
      <div class="app-page-heading">
        <div class="app-page-eyebrow">
          <Package class="h-4 w-4" />
          <span>Catalog queue</span>
        </div>
        <h1 class="app-page-title">Item and stock sync</h1>
        <p class="app-page-subtitle">Run the stock synchronization job and confirm that your product catalog is ready
          for posting.</p>

        <div class="page-badge-strip">
          <Button variant="ghost" size="sm"
            @click="() => { track('btn:ViewItemSyncHistory', 'click'); router.push('/app/tasks?entityType=ITEM') }">
            <History class="h-3.5 w-3.5" />
            View sync history
          </Button>
        </div>
      </div>
      <div class="app-page-actions">
        <Button variant="secondary" :loading="syncing"
          @click="() => { track('btn:SyncAllItems', 'click'); triggerStockSync() }">
          <RefreshCw class="h-4 w-4" />
          {{ syncing ? 'Syncing...' : 'Sync all unlinked' }}
        </Button>
      </div>
    </section>
    <!-- Error state -->
    <Alert v-if="error" variant="error" title="Item sync issue" compact class="page-alert">
      {{ error }}
    </Alert>
    <!-- Success message (202 feedback) -->
    <Alert v-if="successMessage" variant="info" title="Stock sync started" compact class="page-alert">
      {{ successMessage }}
    </Alert>
    <!-- Entity Association Dialog -->
    <EntityAssociationDialog :open="dialogOpen" entityType="item" :source="dialogSource"
      :lytningEntity="dialogLytningEntity ?? undefined" :qboEntity="dialogQboEntity ?? undefined"
      @update:open="dialogOpen = $event" @associated="handleAssociated" />
    <!-- Association section -->
    <section class="assoc-section">
      <!-- Shared search + filter row (above both tabs) -->
      <div class="entity-search">
        <SearchInput
          v-model="searchQuery"
          label="Search items"
          placeholder="Search by id, name, sku"
          :loading="activeTab === 'qbo' && studio.loading.value"
        />
      </div>
      <div class="status-tabs" role="tablist" aria-label="Filter by association status">
        <button
          v-for="tab in ['all', 'associated', 'unassociated', 'syncing'] as const"
          :key="tab"
          type="button"
          role="tab"
          class="status-tab"
          :class="{
            'status-tab--active': sharedFilter === tab,
            'status-tab--disabled': tab === 'syncing' && activeTab === 'qbo',
          }"
          :disabled="tab === 'syncing' && activeTab === 'qbo'"
          :title="tab === 'syncing' && activeTab === 'qbo' ? 'Syncing is a Lytning concept — not available on the QBO tab' : undefined"
          :aria-selected="sharedFilter === tab"
          @click="onSharedFilterClick(tab)"
        >
          {{ tab.charAt(0).toUpperCase() + tab.slice(1) }}
        </button>
      </div>

      <!-- Main tabs -->
      <TabNav v-model="activeTab" :tabs="[
        { id: 'lytning', label: 'Lytning Items', badge: unassociatedCount || undefined },
        { id: 'qbo', label: 'QBO Items' }
      ]" />
      <div v-if="activeTab === 'lytning'">
        <!-- Loading state -->
        <div v-if="associationLoading" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading associations...</span>
        </div>

        <!-- Empty state -->
        <div v-else-if="filteredLytningRows.length === 0" class="tasks-empty">
          <div class="tasks-empty-symbol">
            <Package class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">{{ searchQuery ? 'No matches' : 'No items found' }}</div>
          <div class="tasks-empty-copy">
            {{ searchQuery ? `No items match "${searchQuery}". Try a different search term.` : 'Try a different filter or sync unassociated items first.' }}
          </div>
        </div>

        <!-- Association table -->
        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>LytSoft ID</th>
                <th>Name</th>
                <th>Type</th>
                <th>Price</th>
                <th>Status</th>
                <th>QBO ID</th>
                <th>Synced At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredLytningRows" :key="item.lytsoftItemId">
                <td class="td-mono">{{ item.lytsoftItemId }}</td>
                <td>{{ item.itemName }}</td>
                <td>{{ item.itemType ?? '—' }}</td>
                <td>{{ item.unitPrice != null ? `${item.currencySymbol ?? ''}${item.unitPrice.toFixed(2)}` : '—' }}</td>
                <td>
                  <Badge v-if="item.qboItemId" variant="success">
                    <CheckCircle class="h-3 w-3" />
                    Associated
                  </Badge>
                  <Badge v-else variant="warning">
                    <AlertCircle class="h-3 w-3" />
                    Unassociated
                  </Badge>
                </td>
                <td class="td-mono">{{ item.qboItemId ?? '—' }}</td>
                <td>{{ formatDate(item.syncedAt) }}</td>
                <td>
                  <div class="action-cell">
                    <div v-if="item.runningTaskId" class="sync-indicator">
                      <RefreshCw class="h-4 w-4 spin" />
                      Syncing #{{ item.runningTaskId }}
                    </div>
                    <div v-else class="action-buttons">
                      <Button variant="outline" size="sm"
                        @click="() => { track('btn:SyncItem', 'click'); syncSingleItem(item) }"
                        :loading="syncingRow === item.lytsoftItemId">
                        <RefreshCw class="h-4 w-4" />
                        Sync
                      </Button>
                      <Button variant="ghost" size="sm"
                        @click="() => { track('btn:LinkItemLytning', 'click'); openLinkDialog('lytning', item) }">
                        <Link class="h-4 w-4" />
                        Link
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <Pagination v-model:page="associationPage" v-model:pageSize="associationPageSize" :total="associationTotal"
          :loading="associationLoading" label="items" :page-size-options="[50, 100, 200]" track-label="btn:ItemPage"
          @update:pageSize="onPageSizeChange" />
      </div>

      <!-- QBO tab content -->
      <div v-if="activeTab === 'qbo'" class="qbo-tab">

        <Alert v-if="studio.error.value" variant="error" title="Search failed" class="page-alert">
          {{ studio.error.value }}
        </Alert>

        <div v-if="studio.results.value.length === 0 && !studio.loading.value && !studio.error.value"
          class="tasks-empty">
          <div class="tasks-empty-symbol">
            <RefreshCw class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">No QBO items found</div>
          <div class="tasks-empty-copy">
            Try a different search term.
          </div>
        </div>

        <div v-else-if="studio.loading.value" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading QBO items...</span>
        </div>

        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Description</th>
                <th>Price</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in studio.results.value" :key="item.qboItemId">
                <td>{{ item.name }}</td>
                <td>{{ item.type ?? '—' }}</td>
                <td>{{ item.description?.substring(0, 50) ?? '—' }}</td>
                <td>{{ item.unitPrice != null ? `$${item.unitPrice.toFixed(2)}` : '—' }}</td>
                <td>
                  <Button variant="ghost" size="sm"
                    @click="() => { track('btn:LinkItemQbo', 'click'); openLinkDialog('qbo', item) }">
                    <Link class="h-4 w-4" />
                    Link
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>

          <Pagination :page="studio.page.value" :pageSize="studio.size.value" :total="studio.totalCount.value"
            :loading="studio.loading.value" label="QBO items" :page-size-options="[20, 50]"
            track-label="btn:QboItemPage" @update:pageSize="studio.setSize" />
        </div>
      </div>
    </section>

    <TaskDetailDrawer :task="selectedTask" :open="drawerOpen" @action-complete="loadTaskState"
      @close="drawerOpen = false" />
  </div>
</template>

<style scoped>
.page-alert {
  margin-bottom: 1rem;
}

.page-badge-strip {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

/* Action cell */
.action-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* QBO tab */
.qbo-tab {
  margin-top: 1rem;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 2rem;
  color: var(--color-ink-soft);
}

.status-tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.status-tab {
  padding: 0.375rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-ink-soft);
  cursor: pointer;
  transition: all 0.15s;
}

.status-tab:hover {
  background: var(--color-surface-muted);
}

.status-tab--active {
  background: var(--color-ink);
  color: var(--color-surface);
  border-color: var(--color-border-strong);
}

.status-tab--disabled,
.status-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-tab--disabled:hover,
.status-tab:disabled:hover {
  background: var(--color-surface);
}


.assoc-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 2rem;
  color: var(--color-ink-soft);
}

.assoc-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-md);
}

.assoc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.assoc-table th {
  text-align: left;
  padding: 0.625rem 0.75rem;
  background: var(--color-surface-muted);
  border-bottom: 1px solid var(--color-border-soft);
  font-weight: 600;
  color: var(--color-ink);
  white-space: nowrap;
}

.assoc-table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid var(--color-border-soft);
  color: var(--color-ink);
}

.assoc-table tr:last-child td {
  border-bottom: none;
}

.td-mono {
  font-family: monospace;
  font-size: 0.7rem;
}

.sync-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-ink-soft);
  font-size: 0.75rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 1s linear infinite;
}
</style>